import { httpApi } from '@api/http.api.ts';
import type {
    PATHWAY_MENU_TYPES,
    SECTION_STATUS,
    TASK_STATUS,
} from '@routes/sidebar/___shared___/components/pathway-menu/constants.ts';
import type { PATHWAY_TAG_TYPES } from '@routes/sidebar/learning/___pages___/learning-overview/___shared___/constants.ts';
import type { LearningDetails, LearningUserActivity } from '@routes/sidebar/learning/api/learning.api.ts';

export type PathwayTagType = (typeof PATHWAY_TAG_TYPES)[keyof typeof PATHWAY_TAG_TYPES];
export type LearningElementTypes = (typeof PATHWAY_MENU_TYPES)[keyof typeof PATHWAY_MENU_TYPES];
export type LearningElementStatus = (typeof SECTION_STATUS)[keyof typeof SECTION_STATUS];
export type UserPathwayTaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

export interface LearningOverviewResponse {
    id: number;
    learningDetails: LearningDetails;
    userActivity: LearningUserActivity | null;
    thumbNailURI: string;
    isSubscribed: boolean;
    sections: LearningSection[];
    finalQuiz: LearningElement | null;
    adaptiveQuiz: LearningElement | null;
    pathwayTags: PathwayTag[];
    revisions: Revision[];
    isDemoUser: boolean;
    enforceLinearSequence: boolean;
    lastVisitedElementId: number | null;
    isAdaptiveEnabled: boolean;
    // if false, isAdaptiveEnabled can be true but will be ignored, because global setting has higher priority
    isGlobalAdaptiveEnabled: boolean;
    isOrganizedInSections: boolean;
    hasCertificate: boolean;
    rankIds: number[];
    resultId: number | null;
    isCertificatePendingGeneration: boolean | null;
}

export interface LearningElement {
    id: number;
    name: string;
    order: number;
    durationInSeconds: number;
    learningElementType: LearningElementTypes;
    learningElementState: LearningElementStatus;
    taskStatus: UserPathwayTaskStatus | null;
    embededURL: string | null;
    disableSeekingOnFirstPlay: boolean;
    thumbnailURL: string | null;
    tasksCount: number | null;
    // Video elements only: audio language codes of the video (e.g. ['en', 'nb']); null for other types
    audioTracks: string[] | null;
}

export interface LearningSection {
    name: string | null;
    elements: LearningElement[];
    subSections: LearningSection[];
}

export interface PathwayTag {
    items: string[];
    pathwayTagCategory: PathwayTagType;
}

export interface Revision {
    pathwayId: number;
    version: string;
    dateUTC: string;
    notes: string;
}

export interface GeneralFeedbackRequest {
    elementId: number;
    message: string | null;
    timeInSeconds: number | null;
    quizQuestionId: number | null;
    feedbackType: string;
}

export interface PathwayRateFeedbackRequest {
    message: string;
    pathwayRate: number;
}

export interface PathwayElementRequest {
    elementId: number | null;
}

export const getApiLearningOverview = (id: number): Promise<LearningOverviewResponse> =>
    httpApi.get<LearningOverviewResponse>(`learning/${id}`).then(({ data }) => data);

export const putApiGeneralFeedback = (payload: GeneralFeedbackRequest, pathwayId: string): Promise<void> =>
    httpApi
        .put(`learning/${pathwayId}/feedback`, {
            ...payload,
        })
        .then(() => {});

export const putApiPathwaysRateFeedback = (payload: PathwayRateFeedbackRequest, pathwayId: string): Promise<void> =>
    httpApi
        .put(`learning/${pathwayId}/pathway-rate-feedback`, {
            ...payload,
        })
        .then(() => {});

export const putApiCompletedPathwayElement = (payload: PathwayElementRequest, pathwayId: string): Promise<any> =>
    httpApi
        .put<any>(`learning/${pathwayId}/completed`, {
            ...payload,
        })
        .then(({ data }) => data);

export const putApiVisitedPathwayElement = (pathwayId: string, elementId: number): Promise<any> =>
    httpApi.put<void>(`learning/${pathwayId}/elements/${elementId}/visited`).then(({ data }) => data);

export const postApiResetPathway = (pathwayId: string): Promise<any> =>
    httpApi.post<LearningOverviewResponse>(`learning/${pathwayId}/reset`).then(({ data }) => data);
